import { DoctorUser, Doctor, BPDoctor } from "../../models";

export type State = {
	originalUser: DoctorUser | null;
	originalDoctor: Doctor | null;
	user: DoctorUser | null;
	doctor: Doctor | null;
	availableBpDoctors: BPDoctor[];
	errors: number[];
	isUpdating: boolean;
};

export type Action =
	| {
			type: "init";
			payload: {
				user: DoctorUser;
				doctor: Doctor;
				bpInstances: BPDoctor[];
			};
	  }
	| { type: "set_field"; payload: { path: string[]; value: any } }
	| { type: "set_doctor"; payload: Doctor }
	| { type: "set_available_bp"; payload: BPDoctor[] }
	| { type: "link_bp"; payload: BPDoctor }
	| { type: "unlink_bp"; payload: number }
	| {
			type: "toggle_consult";
			payload: {
				id: number;
				type:
					| "FaceToFace"
					| "Telehealth"
					| "LocalTelehealth"
					| "TakingAppointments";
			};
	  }
	| { type: "set_errors"; payload: number[] }
	| { type: "set_updating"; payload: boolean };

export const reducer = (state: State, action: Action): State => {
	switch (action.type) {
		case "init": {
			const { user, doctor, bpInstances } = action.payload;

			return {
				...state,
				originalUser: user,
				originalDoctor: doctor,
				user: user,
				doctor: doctor,
				availableBpDoctors: bpInstances,
				errors: []
			};
		}
		case "set_field": {
			const { path, value } = action.payload;
			// supports paths like ["doctor", "salutation"] or ["doctor", "bpDoctors", index, "takingTelehealth"]
			const clone = JSON.parse(JSON.stringify(state));
			let cursor: any = clone;
			for (let i = 0; i < path.length - 1; i++) {
				cursor = cursor[path[i]];
				if (cursor === undefined) break;
			}
			if (cursor) cursor[path[path.length - 1]] = value;
			return clone;
		}
		case "set_doctor":
			return { ...state, doctor: action.payload };
		case "set_available_bp":
			return { ...state, availableBpDoctors: action.payload };
		case "link_bp": {
			const bp = action.payload;
			const available = state.availableBpDoctors.filter((b) => b.id !== bp.id);
			const doctorBp = [...(state.doctor?.bpDoctors || []), bp];
			const userDoctor = { ...(state.user || ({} as any)) } as DoctorUser;
			userDoctor.doctor = {
				...(state.doctor || ({} as Doctor)),
				bpDoctors: doctorBp
			} as Doctor;
			return {
				...state,
				availableBpDoctors: available,
				doctor: { ...(state.doctor || {}), bpDoctors: doctorBp } as Doctor,
				user: userDoctor
			};
		}
		case "unlink_bp": {
			const id = action.payload;
			const doctorBp = (state.doctor?.bpDoctors || []).filter(
				(b) => b.id !== id
			);
			const available = [
				...(state.availableBpDoctors || []),
				...(state.doctor?.bpDoctors || []).filter((b) => b.id === id)
			];
			return {
				...state,
				doctor: { ...(state.doctor || {}), bpDoctors: doctorBp } as Doctor,
				availableBpDoctors: available
			};
		}
		case "toggle_consult": {
			const { id, type } = action.payload;
			const doctorBp = (state.doctor?.bpDoctors || []).map((b) => {
				if (b.id !== id) return b;
				const copy = { ...b } as BPDoctor;
				switch (type) {
					case "FaceToFace": {
						copy.takingFaceToFace = !copy.takingFaceToFace;
						break;
					}
					case "Telehealth": {
						copy.takingTelehealth = !copy.takingTelehealth;
						break;
					}
					case "LocalTelehealth": {
						copy.takingLocalTelehealth = !copy.takingLocalTelehealth;
						break;
					}
					case "TakingAppointments": {
						copy.takingAppointments = !copy.takingAppointments;
						break;
					}
				}
				return copy;
			});
			return {
				...state,
				doctor: { ...(state.doctor || {}), bpDoctors: doctorBp } as Doctor
			};
		}
		case "set_errors":
			return { ...state, errors: action.payload };
		case "set_updating":
			return { ...state, isUpdating: action.payload };
		default:
			return state;
	}
};
